import Actions from 'actions'
import useCopilotReports, { buildCopilotReportPath } from 'components/CopilotControl/hooks/useCopilotReports'
import useQueryParams from 'hooks/useQueryParams'
import { ROUTES } from 'consts'
import { Roles } from 'consts/authConsts'
import useBroadcast from 'hooks/useBroadcast'
import usePracticeConfiguration from 'hooks/usePracticeConfiguration'
import useRcDashboard from 'hooks/useRcDashboard'
import useRolePermissions from 'hooks/useRolePermissions'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { trackEvent } from 'utils/analyticsUtils'
import { isUserOfRole } from 'utils/authUtils'

export default ({ source }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const { permissions } = useRolePermissions()
  const { closeBroadcastMode } = useBroadcast()
  const { isRcEnabled } = usePracticeConfiguration()
  const [firstCopilotReport] = useCopilotReports()
  const { queryParams } = useQueryParams()
  const { totalLeads: totalLeadsCount } = useRcDashboard()

  const isAdmin = useMemo(() => isUserOfRole([Roles.Admin]), [])

  const [selectedTab, setSelectedTab] = useState()

  const isRcDashboardEnabled = useMemo(
    () => permissions.rcDashboard && (isRcEnabled || isAdmin),
    [permissions, isRcEnabled, isAdmin]
  )

  const handleClickInvitePatient = useCallback(() => {
    dispatch(Actions.requestOpenInvitePatientModal(true))
    closeBroadcastMode('Invite patient clicked')
    trackEvent('Invite patient - modal opened', {
      source
    })
  }, [source, closeBroadcastMode, dispatch])

  const handleClickCreateRecordsPatient = useCallback(() => {
    dispatch(Actions.setCreateRecordsPatientModalOpen({ isOpen: true }))
    trackEvent('Create records patient - clicked', {
      source
    })
  }, [source, dispatch])

  const tabs = useMemo(
    () =>
      [
        {
          key: 'copilot',
          displayName: t('common.appHeader.tabs.copilot'),
          // Opens the first report the account can open, so an account without Copilot Control never
          // mounts it just to be redirected. The tab stays active on every /copilot report.
          to: firstCopilotReport && buildCopilotReportPath(firstCopilotReport.route, queryParams.copilotDoctorId),
          activePath: ROUTES.COPILOT_CONTROL,
          condition: !!firstCopilotReport
        },
        {
          key: 'patients',
          displayName: t('common.appHeader.tabs.patients'),
          to: ROUTES.PATIENTS,
          condition: true
        },
        {
          key: 'rc',
          displayName: t('common.appHeader.tabs.rcDashboard'),
          badgeValue: totalLeadsCount,
          to: ROUTES.RC_DASHBOARD,
          condition: isRcDashboardEnabled
        },
        {
          key: 'grinsights',
          displayName: t('common.appHeader.tabs.grinsights'),
          to: '/',
          condition: false
        }
      ].filter(({ condition }) => !!condition),
    [isRcDashboardEnabled, firstCopilotReport, queryParams.copilotDoctorId, t, totalLeadsCount]
  )

  const handleTabChange = useCallback(
    (e, newValue) => {
      setSelectedTab(newValue)
      history.push(newValue)
    },
    [history]
  )

  useEffect(() => {
    const activeTab = tabs.find(tab => pathname.includes(tab.activePath || tab.to))?.to
    setSelectedTab(activeTab)
  }, [tabs, pathname])

  return {
    isRcDashboardEnabled,
    selectedTab,
    tabs,
    handleTabChange,
    handleClickInvitePatient,
    handleClickCreateRecordsPatient
  }
}
